import { createFileRoute } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { BlueprintGrid, BpFrame, CoordLabel } from "@/components/blueprint/Blueprint";
import { SEOHead } from "@/components/shared/SEOHead";

export const Route = createFileRoute("/press")({
  component: PressPage,
  validateSearch: (search: Record<string, unknown>) => ({
    print: typeof search.print === "string" ? (search.print as string) : undefined,
  }),
});

/** Wraps the literal string `[CONFIRM]` (and `[CONFIRM …]`) in a
 *  visually flagged span so cofounders can scan the page for open
 *  items. The `.bp-confirm` style is suppressed in print. */
function Confirm({ children }: { children: ReactNode }) {
  return <span className="bp-confirm">{children}</span>;
}

const BOILERPLATE_50 =
  "Quuil is a mobile design and merchandise platform that helps anyone create custom artwork, preview it on real products, and order it from their phone. Start from a photo, sketch, prompt, or The Inkwell community, then customize, checkout, track, and share.";

const CORE_LOOP = [
  {
    label: "Create",
    text: "Start with a photo, live camera capture, AI-assisted prompt, blank draft, or community design.",
  },
  {
    label: "Customize",
    text: "Use the mockup editor to choose a product, color, size, style, print position, and placement.",
  },
  {
    label: "Order",
    text: "Add directly to cart, apply pricing and coupons, choose shipping, checkout with Stripe, and track delivery.",
  },
  {
    label: "Share",
    text: "Publish work to The Inkwell so others can save it, remix it, and discover the next starting point.",
  },
];

const PRODUCT_STORY = [
  {
    title: "Start",
    caption: "Open Quuil, choose a canvas, upload art, or describe an idea.",
    src: "/press-assets/screenshots/Main Home Screen - light.PNG",
  },
  {
    title: "Create",
    caption: "Generate options, pick a style, or edit the design directly.",
    src: "/press-assets/screenshots/describe your idea screen - dark.PNG",
  },
  {
    title: "Edit",
    caption: "Crop, rotate, scale, add text, and refine the artwork before it becomes a product.",
    src: "/press-assets/screenshots/image editor - add text - light.PNG",
  },
  {
    title: "Preview",
    caption: "Place the design on real merchandise with color, variant, and print-area controls.",
    src: "/press-assets/screenshots/Real mockup preview - dark screen.PNG",
  },
  {
    title: "Order",
    caption: "Move from mockup to cart, checkout, and order tracking without leaving the app.",
    src: "/press-assets/screenshots/cart. screen - dark.PNG",
  },
  {
    title: "Share",
    caption: "Publish, discover, save, and reuse designs through The Inkwell community.",
    src: "/press-assets/screenshots/inkwell - dark.PNG",
  },
];

function PressPage() {
  const { print } = Route.useSearch();
  const isLight = print === "light";

  // Scope print stylesheet behavior to this route only.
  useEffect(() => {
    document.body.classList.add("is-printing-press");
    return () => {
      document.body.classList.remove("is-printing-press");
    };
  }, []);

  return (
    <div className={`bp-press-page page-enter ${isLight ? "is-light" : ""}`}>
      <SEOHead
        title="Quuil — Press Kit"
        description={BOILERPLATE_50}
        path="/press"
        robots="noindex,nofollow"
      />

      <section className="bp-press-doc">
        <BlueprintGrid light />

        <div className="bp-hero-border" aria-hidden="true">
          <div className="bp-title-block">
            <span>Project</span>
            <strong>Quuil — Press Kit</strong>
            <small>DWG NO. QUL-PR-001 // SCALE 1:1</small>
          </div>
          <CoordLabel className="bp-border-a1">A1</CoordLabel>
          <CoordLabel className="bp-border-a8">A8</CoordLabel>
          <CoordLabel className="bp-border-h1">H1</CoordLabel>
          <CoordLabel className="bp-border-h8">H8</CoordLabel>
        </div>

        <header style={{ position: "relative", zIndex: 1 }}>
          <span className="bp-kicker">For Press // Product Overview</span>
          <h1 className="bp-press-headline">
            Create. Customize. Order. <span>Share.</span>
          </h1>
          <p className="bp-subtitle" style={{ margin: "1.5rem 0 0", maxWidth: 720 }}>
            Quuil lets people make custom artwork, place it on real merchandise,
            buy it, track it, and share it with the community — all from a phone.
            AI-assisted generation is one creation tool in a larger design,
            commerce, and discovery loop.
          </p>
        </header>

        <span className="bp-press-section-title">Product Walkthrough // Screens</span>
        <div className="bp-screen-story-grid" aria-label="Quuil product walkthrough screenshots">
          {PRODUCT_STORY.map((screen, index) => (
            <BpFrame key={screen.title} label={`0${index + 1} // ${screen.title.toUpperCase()}`}>
              <figure className="bp-screen-card">
                <img src={screen.src} alt={`Quuil ${screen.title} screen`} loading="lazy" />
                <figcaption>
                  <strong>{screen.title}</strong>
                  <span>{screen.caption}</span>
                </figcaption>
              </figure>
            </BpFrame>
          ))}
        </div>

        <span className="bp-press-section-title">Fact Sheet</span>
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <BpFrame label="01">
            <dl className="bp-press-fact">
              <dt>Founded</dt>
              <dd>2024</dd>
            </dl>
          </BpFrame>
          <BpFrame label="02">
            <dl className="bp-press-fact">
              <dt>Launched</dt>
              <dd>2026</dd>
            </dl>
          </BpFrame>
          <BpFrame label="03">
            <dl className="bp-press-fact">
              <dt>Available On</dt>
              <dd>iOS App Store // quuil.com</dd>
            </dl>
          </BpFrame>
          <BpFrame label="04">
            <dl className="bp-press-fact">
              <dt>Based In</dt>
              <dd>Austin, TX</dd>
            </dl>
          </BpFrame>
          <BpFrame label="05">
            <dl className="bp-press-fact">
              <dt>Founders</dt>
              <dd>
                Nel Toms (Cofounder) // Jake Biller (Cofounder) — bios{" "}
                <Confirm>[CONFIRM]</Confirm>
              </dd>
            </dl>
          </BpFrame>
          <BpFrame label="06">
            <dl className="bp-press-fact">
              <dt>Funding</dt>
              <dd>Bootstrapped to date; raising pre-seed angel round</dd>
            </dl>
          </BpFrame>
        </div>

        <span className="bp-press-section-title">Boilerplate // 50 Words</span>
        <BpFrame label="BOILERPLATE">
          <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.65 }}>
            {BOILERPLATE_50}
          </p>
        </BpFrame>

        <span className="bp-press-section-title">How It Works // Core Loop</span>
        <div style={{ display: "grid", gap: "0.6rem" }}>
          {CORE_LOOP.map((step, index) => (
            <BpFrame key={step.label} label={`0${index + 1} // ${step.label.toUpperCase()}`}>
              <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.65 }}>
                {step.text}
              </p>
            </BpFrame>
          ))}
        </div>

        <span className="bp-press-section-title">Quotes // Pre-Cleared</span>
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <BpFrame label="QUOTE 01">
            <div className="bp-press-quote">
              <Confirm>[CONFIRM]</Confirm>
              <blockquote>
                "Quuil takes you from a photo, sketch, prompt, or community design to a physical product from your phone."
              </blockquote>
              <cite>
                <Confirm>[Founder TBD]</Confirm>, Cofounder, Quuil
              </cite>
            </div>
          </BpFrame>
          <BpFrame label="QUOTE 02">
            <div className="bp-press-quote">
              <Confirm>[CONFIRM]</Confirm>
              <blockquote>
                "AI helps when it should, but the product is the full loop: design, mockup, checkout, order tracking, and community discovery."
              </blockquote>
              <cite>
                <Confirm>[Founder TBD]</Confirm>, Cofounder, Quuil
              </cite>
            </div>
          </BpFrame>
        </div>

        <span className="bp-press-section-title">What Makes Quuil Different</span>
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <BpFrame label="01 // FULL LOOP">
            <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.65 }}>
              Design and commerce live in one app. Most tools stop at the file;
              Quuil helps users make the product.
            </p>
          </BpFrame>
          <BpFrame label="02 // MOBILE NATIVE">
            <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.65 }}>
              The workflow is built for the phone: capture, edit, mock up, buy,
              and share without moving to a desktop tool.
            </p>
          </BpFrame>
          <BpFrame label="03 // COMMUNITY POWERED">
            <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.65 }}>
              The Inkwell turns shared designs into browsable inspiration and
              starting points for the next creator.
            </p>
          </BpFrame>
        </div>

        <span className="bp-press-section-title">Press Contact</span>
        <BpFrame label="CONTACT" className="bp-press-contact-card">
          <strong>Cofounders direct — no separate press desk</strong>
          <p style={{ margin: "0.75rem 0 0", fontFamily: "var(--bp-mono)", letterSpacing: "0.08em" }}>
            Nel Toms — <a href="mailto:nel@quuil.com">nel@quuil.com</a>
            {"  //  "}
            Jake Biller — <a href="mailto:jake@quuil.com">jake@quuil.com</a>
          </p>
        </BpFrame>

        <span className="bp-press-section-title">Downloadable Assets</span>
        <ul className="bp-press-asset-list">
          <li>
            <a href="/press-assets/quuil-logo-pack.zip" download>
              <span>Logo lockup pack</span>
              <span>.ZIP // PENDING</span>
            </a>
          </li>
          <li>
            <a
              href="/press?print=light"
              onClick={(event) => {
                event.preventDefault();
                window.location.search = "?print=light";
                window.print();
              }}
            >
              <span>Press one-pager (this page, print-ready)</span>
              <span>.PDF // PRINT</span>
            </a>
          </li>
          <li>
            <a href="/press-assets/quuil-boilerplate.txt" download>
              <span>Boilerplate copy</span>
              <span>.TXT</span>
            </a>
          </li>
          <li>
            <a href="/press-assets/quuil-founder-bios.txt" download>
              <span>Founder bios</span>
              <span>.TXT // PENDING</span>
            </a>
          </li>
          <li>
            <a href="/press-assets/quuil-hero-imagery.zip" download>
              <span>Hero imagery</span>
              <span>.ZIP // PENDING</span>
            </a>
          </li>
          <li>
            <a href="/press-assets/quuil-press-contact.vcf" download>
              <span>Press contact vCard</span>
              <span>.VCF</span>
            </a>
          </li>
        </ul>

        <p
          style={{
            marginTop: "3rem",
            color: "var(--bp-dim)",
            fontFamily: "var(--bp-mono)",
            fontSize: "0.7rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          QUL-PR-001 // SUMMIT MADRID 2026 // PRINT VIEW{" "}
          {isLight ? "(LIGHT)" : (
            <>
              —{" "}
              <a href="/press?print=light" style={{ color: "var(--bp-yellow)" }}>
                Switch to print-light
              </a>
            </>
          )}
        </p>
      </section>
    </div>
  );
}
